import { useEffect, useState } from "react";
import { Users, Storefront, CheckCircle, Clock, Receipt, Coins, Gift, Warning, Prohibit, TrendUp } from "@phosphor-icons/react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { pts } from "@/lib/format";
import { Button } from "@/components/ui/button";

const tooltipStyle = { background: "#0f1a14", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" };

const Stat = ({ icon: Icon, label, value, accent, warn }) => (
  <div className="card-surface p-4 sm:p-5 fade-up">
    <Icon size={22} weight="duotone" className={warn ? "text-amber-600 mb-3" : accent ? "text-primary mb-3" : "text-muted-foreground mb-3"} />
    <p className={`font-display font-bold text-2xl sm:text-3xl ${warn ? "text-amber-600" : accent ? "text-primary" : ""}`}>{value}</p>
    <p className="text-xs text-muted-foreground mt-1">{label}</p>
  </div>
);

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  const [error, setError] = useState(false);
  const load = () => { setError(false); api.get("/admin/dashboard").then((r) => setD(r.data)).catch(() => setError(true)); };
  useEffect(() => { load(); }, []);
  if (error && !d) return (
    <div className="pt-16 text-center space-y-3" data-testid="dashboard-error">
      <p className="text-muted-foreground">Couldn't load the platform overview.</p>
      <Button onClick={load} variant="outline" className="rounded-xl border-black/10">Retry</Button>
    </div>
  );
  if (!d) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6">
      <div><h1 className="font-display font-bold text-2xl sm:text-3xl">Platform overview</h1><p className="text-muted-foreground text-sm">The health of the OneKite network.</p></div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Stat icon={Users} label="Total customers" value={d.total_customers} accent />
        <Stat icon={Storefront} label="Total businesses" value={d.total_businesses} />
        <Stat icon={CheckCircle} label="Active businesses" value={d.active_businesses} accent />
        <Stat icon={Clock} label="Pending approval" value={d.pending_businesses} warn={d.pending_businesses > 0} />
        <Stat icon={Receipt} label="Transactions today" value={d.transactions_today} />
        <Stat icon={Coins} label="Points issued" value={pts(d.points_issued)} accent />
        <Stat icon={Gift} label="Points redeemed" value={pts(d.points_redeemed)} />
        <Stat icon={Prohibit} label="Auto-blocked" value={d.auto_blocked ?? 0} warn={(d.auto_blocked ?? 0) > 0} />
      </div>

      <div className="card-surface p-4 sm:p-6" data-testid="network-trend-chart">
        <h2 className="font-display font-semibold mb-1 flex items-center gap-2"><TrendUp size={18} className="text-primary" /> Network points activity</h2>
        <p className="text-xs text-muted-foreground mb-4">Last 14 days across all businesses</p>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={d.daily} margin={{ left: -18, right: 8 }}>
            <defs>
              <linearGradient id="aIssued" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#34d399" stopOpacity={0.5} /><stop offset="100%" stopColor="#34d399" stopOpacity={0} /></linearGradient>
              <linearGradient id="aRedeemed" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#f59e0b" stopOpacity={0.4} /><stop offset="100%" stopColor="#f59e0b" stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
            <XAxis dataKey="date" tick={{ fill: "#7d8a83", fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
            <YAxis tick={{ fill: "#7d8a83", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Area type="monotone" dataKey="issued" stroke="#34d399" strokeWidth={2} fill="url(#aIssued)" name="Issued" />
            <Area type="monotone" dataKey="redeemed" stroke="#f59e0b" strokeWidth={2} fill="url(#aRedeemed)" name="Redeemed" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="card-surface p-4 sm:p-6" data-testid="top-businesses-chart">
        <h2 className="font-display font-semibold mb-4">Top businesses by transactions</h2>
        {(!d.top_businesses || d.top_businesses.length === 0) ? <p className="text-sm text-muted-foreground">No transactions yet.</p> : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={d.top_businesses} layout="vertical" margin={{ left: 10, right: 16 }}>
              <XAxis type="number" tick={{ fill: "#7d8a83", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={130} tick={{ fill: "#7d8a83", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "rgba(52,211,153,0.08)" }} contentStyle={tooltipStyle} />
              <Bar dataKey="transactions" radius={[0, 8, 8, 0]}>{d.top_businesses.map((_, i) => <Cell key={i} fill="#34d399" />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
